import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/data-analytics';
const AUDIO_VERSION = 'v5';

/** Hardcoded demo file representing a sample sales CSV */
const DEMO_FILE = {
  fileName: 'quarterly_sales.csv',
  fileSize: 2450000,
  uploadDate: '2026-02-23T10:00:00Z',
  s3Key: 'users/demo/quarterly_sales.csv',
  contentType: 'text/csv',
};

/** Mock schema for quarterly sales data */
const DEMO_SCHEMA = [
  { name: 'row_id', data_type: 'INT64' },
  { name: 'product', data_type: 'BYTE_ARRAY' },
  { name: 'region', data_type: 'BYTE_ARRAY' },
  { name: 'sales_amount', data_type: 'DOUBLE' },
  { name: 'units_sold', data_type: 'INT64' },
  { name: 'quarter', data_type: 'BYTE_ARRAY' },
  { name: 'rep_name', data_type: 'BYTE_ARRAY' },
  { name: 'category', data_type: 'BYTE_ARRAY' },
];

/** Mock rows of quarterly sales data */
const DEMO_ROWS = [
  { row_id: 1, product: 'BigSplice Enterprise', region: 'North America', sales_amount: 128500, units_sold: 12, quarter: 'Q4 2025', rep_name: 'Alex Rivera', category: 'Enterprise Deal' },
  { row_id: 2, product: 'DataSync Pro', region: 'Europe', sales_amount: 45200, units_sold: 38, quarter: 'Q4 2025', rep_name: 'Nina Patel', category: 'Mid-Market' },
  { row_id: 3, product: 'BigSplice Starter', region: 'Asia Pacific', sales_amount: 18700, units_sold: 95, quarter: 'Q3 2025', rep_name: 'James Park', category: 'SMB' },
  { row_id: 4, product: 'BigSplice Enterprise', region: 'Europe', sales_amount: 215000, units_sold: 8, quarter: 'Q4 2025', rep_name: 'Sophie Laurent', category: 'Enterprise Deal' },
  { row_id: 5, product: 'Analytics Add-On', region: 'North America', sales_amount: 32400, units_sold: 62, quarter: 'Q3 2025', rep_name: 'Alex Rivera', category: 'Upsell' },
  { row_id: 6, product: 'BigSplice Enterprise', region: 'North America', sales_amount: 175000, units_sold: 5, quarter: 'Q4 2025', rep_name: 'Marcus Chen', category: 'Enterprise Deal' },
];

/** Mock search results for "enterprise deal" query */
const DEMO_SEARCH_RESULTS = [
  { row_id: 1, product: 'BigSplice Enterprise', region: 'North America', sales_amount: 128500, category: 'Enterprise Deal' },
  { row_id: 4, product: 'BigSplice Enterprise', region: 'Europe', sales_amount: 215000, category: 'Enterprise Deal' },
  { row_id: 6, product: 'BigSplice Enterprise', region: 'North America', sales_amount: 175000, category: 'Enterprise Deal' },
];

/**
 * Data Analytics demo script with LIVE DATA injection.
 * Uses hardcoded quarterly_sales.csv with mock sales data
 * flowing through all 4 nodes of the data-analytics flow template.
 */
export const dataAnalyticsScript: DemoScript = {
  id: 'data-analytics',
  templateId: 'data-analytics',
  name: 'Data Analytics Demo',
  description: 'Learn how to upload, explore, and search structured data files.',
  steps: [
    // === INTRO (no target) ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: 'Let me walk you through a data analytics pipeline \u2014 we\'ll go from a raw CSV file to instant full-text search in just a few steps.',
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 7500 },
    },

    // === STEP 1: Upload Data ===
    {
      id: 'upload-highlight',
      type: 'highlight',
      target: { nodeId: 'upload' },
      delayBefore: 200,
      subtitle: 'This is your data landing zone. Drag and drop CSV, Parquet, or Excel files \u2014 BigSplice handles them all.',
      audio: { src: `${AUDIO_BASE}/upload-highlight.mp3?${AUDIO_VERSION}`, duration: 7500 },
      cursorPath: [
        { x: -80, y: -40, duration: 600 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'upload-sample-note',
      type: 'narrate',
      target: { nodeId: 'upload' },
      subtitle: 'This demo runs on simulated sales data so you can see the entire flow without waiting. Upload your own spreadsheet afterward and it\u2019ll process it for real.',
      audio: { src: `${AUDIO_BASE}/upload-sample-note.mp3?${AUDIO_VERSION}`, duration: 10500 },
    },
    {
      id: 'upload-decision',
      type: 'decision',
      target: { nodeId: 'upload' },
      subtitle: 'Want me to use our sample sales dataset, or would you like to upload your own file?',
      audio: { src: `${AUDIO_BASE}/upload-decision.mp3?${AUDIO_VERSION}`, duration: 4500 },
      decision: {
        prompt: 'Want me to use our sample dataset, or would you like to upload a file?',
        options: [
          { label: 'Use sample dataset', icon: 'file-spreadsheet', nextStepId: 'upload-inject-uploading' },
          { label: 'I\u2019ll upload mine', icon: 'upload', nextStepId: 'upload-interact' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'upload-interact',
      type: 'interact',
      target: { nodeId: 'upload' },
      subtitle: 'Go ahead and drag a file onto the upload area. I\'ll wait for you.',
      audio: { src: `${AUDIO_BASE}/upload-interact.mp3?${AUDIO_VERSION}`, duration: 6500 },
      autoAdvanceAfter: 4000,
    },

    // === INJECT: Simulate file upload progress ===
    {
      id: 'upload-inject-uploading',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Uploading file\u2026',
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'quarterly_sales.csv', progress: 35 } },
      },
      autoAdvanceAfter: 250,
    },
    {
      id: 'upload-inject-progress',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'quarterly_sales.csv', progress: 72 } },
      },
      autoAdvanceAfter: 200,
    },
    {
      id: 'upload-inject-almost',
      type: 'inject',
      target: { nodeId: 'upload' },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'uploading', fileName: 'quarterly_sales.csv', progress: 95 } },
      },
      autoAdvanceAfter: 150,
    },
    {
      id: 'upload-inject-done',
      type: 'inject',
      target: { nodeId: 'upload' },
      subtitle: 'Upload complete. Your data is ready to explore.',
      audio: { src: `${AUDIO_BASE}/upload-inject.mp3?${AUDIO_VERSION}`, duration: 3000 },
      inject: {
        nodeId: 'upload',
        data: { demoState: { status: 'done', fileName: 'quarterly_sales.csv' } },
      },
    },

    // === STEP 2: View Data ===
    {
      id: 'dataset-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 200,
      subtitle: 'Now let\u2019s view the data we just uploaded and start exploring.',
      audio: { src: `${AUDIO_BASE}/dataset-highlight.mp3?${AUDIO_VERSION}`, duration: 3500 },
      cursorPath: [
        { x: -40, y: -30, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },
    // Pre-inject mock data into downstream nodes BEFORE file selection triggers them
    {
      id: 'sheets-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'sheets',
        data: {
          demoState: {
            schema: DEMO_SCHEMA,
            rows: DEMO_ROWS,
            rowCount: 12480,
          },
        },
      },
      autoAdvanceAfter: 100,
    },
    {
      id: 'search-pre-inject',
      type: 'inject',
      inject: {
        nodeId: 'search',
        data: {
          demoState: {},
        },
      },
      autoAdvanceAfter: 100,
    },
    // Now inject the file \u2014 downstream nodes already have demoState and won\u2019t call APIs
    {
      id: 'dataset-inject',
      type: 'inject',
      target: { nodeId: 'sheets' },
      subtitle: 'Loading data\u2026 Watch the data flow through the pipeline.',
      audio: { src: `${AUDIO_BASE}/dataset-selected.mp3?${AUDIO_VERSION}`, duration: 5000 },
      inject: {
        nodeId: 'sheets',
        data: { selectedFile: DEMO_FILE },
      },
    },

    // === STEP 3: Browse Data (mock data already loaded via demoState) ===
    {
      id: 'sheets-highlight',
      type: 'highlight',
      target: { nodeId: 'sheets' },
      delayBefore: 400,
      subtitle: 'Here\'s your data in spreadsheet form. Every row, every column \u2014 sortable, scrollable, and ready to analyze.',
      audio: { src: `${AUDIO_BASE}/sheets-highlight.mp3?${AUDIO_VERSION}`, duration: 8500 },
      cursorPath: [
        { x: -100, y: -60, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 50, y: 30, duration: 400 },
      ],
    },
    {
      id: 'sheets-decision',
      type: 'decision',
      target: { nodeId: 'sheets' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data?',
        options: [
          { label: 'Explore the data', icon: 'search', nextStepId: 'sheets-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },
    {
      id: 'sheets-interact',
      type: 'interact',
      target: { nodeId: 'sheets' },
      subtitle: 'Take a moment to browse through the data. Click column headers to sort, or scroll to explore more rows.',
      audio: { src: `${AUDIO_BASE}/sheets-interact.mp3?${AUDIO_VERSION}`, duration: 7500 },
      autoAdvanceAfter: 4000,
    },

    // === STEP 4: Full-Text Search ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { nodeId: 'search' },
      subtitle: 'Full-text search across every column. Find specific products, regions, or deal types in milliseconds.',
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 6500 },
      cursorPath: [
        { x: -60, y: -40, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },
    {
      id: 'search-decision',
      type: 'decision',
      target: { nodeId: 'search' },
      subtitle: '',
      decision: {
        prompt: 'Want to try a search?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-interact' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },
    {
      id: 'search-demo',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Let me search for enterprise deals \u2014 watch this.',
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 3000 },
      interaction: { action: 'type', delay: 100, value: 'enterprise deal' },
      autoAdvanceAfter: 1200,
    },
    // Inject mock search results after typing
    {
      id: 'search-inject-results',
      type: 'inject',
      target: { nodeId: 'search' },
      inject: {
        nodeId: 'search',
        data: {
          demoState: {
            results: DEMO_SEARCH_RESULTS,
            resultCount: 3,
          },
        },
      },
      autoAdvanceAfter: 1200,
    },
    {
      id: 'search-interact',
      type: 'interact',
      target: { nodeId: 'search' },
      subtitle: 'Type any search term into the search box. Try "North America" or "Q4 2025" to filter your data.',
      audio: { src: `${AUDIO_BASE}/search-interact.mp3?${AUDIO_VERSION}`, duration: 7500 },
      autoAdvanceAfter: 4000,
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: 'That\'s the complete pipeline \u2014 from raw data file to searchable analytics in seconds. Ready to try it with your own data?',
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 8000 },
    },
    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Start my own flow', icon: 'rocket', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
